import React, { useEffect } from "react";
import "./App.scss";
import { BrowserRouter } from "react-router-dom";
import { styled } from "@mui/material";
import ApplicationFooter from "./components/ApplicationFooter";
import ApplicationHeader from "./components/ApplicationHeader";
import colors from "./theme/themeConstants";
import ProfileMenu from "./components/UI/ProfileMenu";
import { useSelector, useDispatch } from "react-redux";
import { IAppState } from "./store/app-state";
import BurgerMenu from "./components/UI/BurgerMenu";
import ResponseMessage from "./components/UI/ResponseMessage";
import { hideAllMenus } from "store/application/application-action";
import { fetchExperiments } from "store/experiments/actions";
import RoutesComponents from "./components/RoutesComponents";
import GoCtrl from "./components/ads/goCtrl";
import { QueryClient, QueryClientProvider } from "react-query";
import { PreferencesPopup } from "components/UI/PreferencesPopup";
import { CookieNoticeWrapper } from "components/widgets";
import AuthPopup from "./components/UI/Popups/AuthPopup/AuthPopup";
import { className } from "utils/strings";
import { useDeviceType } from "hooks";
import TagManager from "react-gtm-module";
import ScrollToTop from "components/UI/ScrollToTop";
import { ChangedEmailPopup } from "./components/UI/Popups/ChangedEmailPopup/ChangedEmailPopup";

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM_ID ?? ""
};

TagManager.initialize(tagManagerArgs);

const isSpecialPage = (): boolean => {
  const path = window.location.pathname;

  if (path.indexOf("/ifr/") === 0) {
    return true;
  }

  return false;
};

const App: React.FC = () => {
  const isProfileMenuOpen = useSelector((state: IAppState) => state.application.isProfileMenuOpen);
  const isBurgerMenuOpen = useSelector((state: IAppState) => state.application.isBurgerMenuOpen);
  const isSearchBarOpen = useSelector((state: IAppState) => state.application.isSearchBarOpen);
  const deviceType = useDeviceType();
  const { show: isOpenAuthModal } = useSelector((state: IAppState) => state.application.authModal);
  const dispatch = useDispatch();

  useEffect(() => {
    const body = document.body;
    if (isProfileMenuOpen || isBurgerMenuOpen || isSearchBarOpen) {
      // eslint-disable-next-line no-magic-numbers
      if (window.innerWidth > 1170 && isProfileMenuOpen) {
        return;
      }
      body.style.overflow = "hidden";
    } else {
      body.style.overflow = "auto";
    }
  }, [isProfileMenuOpen, isBurgerMenuOpen, isSearchBarOpen]);

  // Pre-load experiments to speed-up avaiability to components.
  useEffect(() => {
    dispatch(fetchExperiments());
  });

  const handleBindedKey = (event: React.KeyboardEvent) => {
    if (event.code === "Escape") {
      dispatch(hideAllMenus());
    }
  };

  useEffect(() => {
    if (document.head.querySelectorAll("title").length === 0) {
      document.title = "";
    }
  }, []);

  const queryClient = new QueryClient();

  const showNavigation = !isSpecialPage();

  return (
    <QueryClientProvider client={queryClient}>
      <Container onKeyDown={handleBindedKey} className={className({
        "App": true,
        "nav": showNavigation,
        "nonav": !showNavigation,
        [deviceType]: true,
      })}>
        <BrowserRouter>
          <ScrollToTop>
            <GoCtrl />

            {showNavigation && <ApplicationHeader />}

            {isBurgerMenuOpen && <BurgerMenu isBurgerMenuOpen={isBurgerMenuOpen} />}

            {isProfileMenuOpen && <ProfileMenu isProfileMenuOpen={isProfileMenuOpen} />}

            <ResponseMessage />

            {isOpenAuthModal && <AuthPopup />}

            <Wrapper>
              {showNavigation && (
                <div className="navWrapper">
                  <ApplicationFooter />
                </div>
              )}

              <div className="routeWrapper">
                <RoutesComponents />
              </div>
            </Wrapper>
          </ScrollToTop>
          <ChangedEmailPopup />
        </BrowserRouter>
        { !isSpecialPage() && (
          <>
            <PreferencesPopup />
            <CookieNoticeWrapper />
          </>
        )}
      </Container >
    </QueryClientProvider>
  );
};

const Container = styled("div")({
  position: "relative",
  backgroundColor: `${colors.backgroundBlack}`,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const Wrapper = styled("div")({
  display: "flex",
  width: "100%",
  minHeight: "calc(100vh - 68px)",
  justifyContent: "flex-start"
});

export default App;
